import React, { useState, useRef } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import Navbar from "../components/Navbar";
import { useAuth, fmtErr } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Stethoscope, User, Upload, FileCheck2, X, Eye, EyeOff, LogIn } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

const MAX_CERT_MB = 10;
const ALLOWED_CERT = /\.(pdf|jpg|jpeg|png|webp)$/i;

export default function Signup() {
  const { signup } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [params] = useSearchParams();
  // Signup page is now DOCTOR-ONLY. Patients create accounts from the
  // login page (mobile OTP automatically creates a patient account).
  const role = "doctor";

  const [form, setForm] = useState({
    name: "", email: "", password: "", phone: "",
    specialization: "Neurology", registration_number: "", experience_years: 5, consultation_fee: 1000,
    fellowships: "",
  });
  const [regFile, setRegFile] = useState(null);   // Registration certificate
  const [degFile, setDegFile] = useState(null);   // Degree certificate
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // Doctor login tab state
  const { login } = useAuth();
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [showPwd, setShowPwd] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginErr, setLoginErr] = useState("");

  const doLogin = async (e) => {
    e.preventDefault();
    setLoginErr(""); setLoginLoading(true);
    try {
      const u = await login(loginForm.email, loginForm.password);
      toast.success("Welcome back!");
      if (u.role === "doctor") nav("/doctor");
      else if (u.role === "admin") nav("/admin");
      else nav("/patient");
    } catch (e) { setLoginErr(fmtErr(e.response?.data?.detail) || e.message); }
    finally { setLoginLoading(false); }
  };

  // Forward booking-intent params (kept for parity with older deep-links)
  const _forwardQS = () => {
    const keep = ["tag", "priority", "doctor", "scan"];
    const out = new URLSearchParams();
    keep.forEach((k) => {
      const v = params.get(k);
      if (v) out.set(k, v);
    });
    const s = out.toString();
    return s ? `?${s}` : "";
  };
  // Kept for future use — patients no longer land here but deep-link can exist.
  void _forwardQS;

  const update = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  const _validateCert = (file) => {
    if (!file) return null;
    if (!ALLOWED_CERT.test(file.name)) return "Only PDF, JPG, PNG, WEBP files allowed.";
    if (file.size > MAX_CERT_MB * 1024 * 1024) return `File must be under ${MAX_CERT_MB} MB.`;
    return null;
  };

  const uploadCert = async (file, purpose) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("purpose", purpose);
    const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.id;
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    // Doctor-specific client-side validation
    if (role === "doctor") {
      if (!regFile) { setErr("Please upload your registration certificate."); return; }
      if (!degFile) { setErr("Please upload your degree certificate."); return; }
      const v1 = _validateCert(regFile); if (v1) { setErr(v1); return; }
      const v2 = _validateCert(degFile); if (v2) { setErr(v2); return; }
    }
    setLoading(true);
    try {
      // 1. Create the doctor account
      const payload = {
        ...form,
        role,
        experience_years: Number(form.experience_years),
        consultation_fee: Number(form.consultation_fee),
      };
      delete payload.fellowships; // saved via /doctors/me/credentials below
      const u = await signup(payload);

      // 2. Upload certificates + save fellowships
      try {
        toast.info("Uploading your certificates…");
        const regId = await uploadCert(regFile, "doctor_registration");
        const degId = await uploadCert(degFile, "doctor_degree");
        const credFd = new FormData();
        credFd.append("registration_file_id", regId);
        credFd.append("degree_file_id", degId);
        if (form.fellowships.trim()) credFd.append("fellowships", form.fellowships.trim());
        await api.put("/doctors/me/credentials", credFd, { headers: { "Content-Type": "multipart/form-data" } });
      } catch (uploadErr) {
        toast.error("Account created, but certificate upload failed. You can re-upload from onboarding.");
      }

      if (u.status === "pending") {
        toast.success("Application submitted.");
        nav("/doctor/onboarding", { state: { name: u.name, email: u.email } });
      } else {
        toast.success("Welcome!");
        nav("/doctor");
      }
    } catch (e) { setErr(fmtErr(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-xl mx-auto px-4 py-10">
        <Card className="p-6 sm:p-8 border-slate-200 rounded-2xl shadow-sm">
          <div className="w-11 h-11 rounded-xl bg-teal-50 border border-teal-100 grid place-items-center text-teal-700 mb-3">
            <Stethoscope className="w-6 h-6"/>
          </div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Doctor signup / Login</h1>
          <p className="text-sm text-slate-500 mt-1">
            Join the NeuroOpinion India specialist network, or sign in to your account. We verify every MCI/NMC registration before activation.
          </p>

          <Tabs defaultValue="signup" className="mt-5">
            <TabsList className="grid grid-cols-2 w-full bg-slate-100 p-1 rounded-xl h-auto">
              <TabsTrigger
                value="signup"
                data-testid="tab-doctor-signup"
                className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-teal-700 py-2.5 text-sm font-medium"
              >
                <Stethoscope className="w-4 h-4 mr-1.5"/> Apply as doctor
              </TabsTrigger>
              <TabsTrigger
                value="login"
                data-testid="tab-doctor-login"
                className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-teal-700 py-2.5 text-sm font-medium"
              >
                <LogIn className="w-4 h-4 mr-1.5"/> Login
              </TabsTrigger>
            </TabsList>

            <TabsContent value="signup" className="mt-6">
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>{t("name")}</Label>
              <Input required value={form.name} onChange={update("name")} data-testid="signup-name-input"/>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>{t("email")}</Label>
                <Input type="email" required value={form.email} onChange={update("email")} data-testid="signup-email-input"/>
              </div>
              <div>
                <Label>Mobile number</Label>
                <Input placeholder="+91XXXXXXXXXX" value={form.phone} onChange={update("phone")} data-testid="signup-phone-input"/>
              </div>
            </div>
            <div>
              <Label>{t("password")}</Label>
              <Input type="password" required minLength={6} value={form.password} onChange={update("password")} data-testid="signup-password-input"/>
              <p className="text-xs text-slate-500 mt-1">Minimum 6 characters.</p>
            </div>

            <div className="space-y-4 border-t border-slate-200 pt-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>{t("specialization")}</Label>
                  <Select value={form.specialization} onValueChange={(v) => setForm({...form, specialization: v})}>
                    <SelectTrigger data-testid="signup-spec-select"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Neurology">Neurology</SelectItem>
                      <SelectItem value="Neurosurgery">Neurosurgery</SelectItem>
                      <SelectItem value="Pediatric Neurology">Pediatric Neurology</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t("experience")}</Label>
                  <Input type="number" min={0} value={form.experience_years} onChange={update("experience_years")} data-testid="signup-exp-input"/>
                </div>
              </div>
              <div>
                <Label>{t("reg_no")}</Label>
                <Input required value={form.registration_number} onChange={update("registration_number")} data-testid="signup-regno-input"/>
              </div>

              {/* Certificate uploads — REQUIRED for doctors */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <CertUpload
                  label="Registration Certificate"
                  hint="MCI / NMC registration · PDF or image"
                  testid="signup-reg-cert"
                  file={regFile}
                  onChange={setRegFile}
                  required
                />
                <CertUpload
                  label="Degree Certificate"
                  hint="MBBS / MD / MCh · PDF or image"
                  testid="signup-deg-cert"
                  file={degFile}
                  onChange={setDegFile}
                  required
                />
              </div>

                {/* Fellowships — OPTIONAL */}
                <div>
                  <Label>
                    Fellowships &nbsp;<span className="text-xs font-normal text-slate-400">· optional</span>
                  </Label>
                  <Textarea
                    placeholder="e.g. Fellowship in Neuroendovascular Surgery (AIIMS 2018), IFNE Paris 2020…"
                    value={form.fellowships}
                    onChange={update("fellowships")}
                    data-testid="signup-fellowships-input"
                    rows={2}
                    maxLength={500}
                  />
                  <p className="text-xs text-slate-500 mt-1">Mention any additional fellowships or sub-specialty training.</p>
                </div>

                <p className="text-xs text-slate-500">
                  Your account &amp; certificates will be reviewed by our admin team before activation. Typical turnaround: <b>24–48 hrs</b>.
                </p>
              </div>

            {err && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2" data-testid="signup-error">{err}</div>}
            <Button disabled={loading} type="submit" className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white rounded-full font-semibold shadow-md" data-testid="signup-submit-btn">
              {loading ? "Submitting…" : "Submit application"}
            </Button>
          </form>
            </TabsContent>

            <TabsContent value="login" className="mt-6">
              <form onSubmit={doLogin} className="space-y-4">
                <div>
                  <Label className="text-xs font-medium text-slate-600 uppercase tracking-wider">Email</Label>
                  <Input
                    type="email"
                    required
                    value={loginForm.email}
                    onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                    data-testid="doctor-login-email-input"
                    className="h-11 mt-1.5 border-slate-300"
                    placeholder="doctor@hospital.com"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-medium text-slate-600 uppercase tracking-wider">Password</Label>
                    <Link to="/forgot-password" className="text-xs text-teal-700 font-semibold hover:underline" data-testid="doctor-forgot-link">
                      Forgot?
                    </Link>
                  </div>
                  <div className="relative mt-1.5">
                    <Input
                      type={showPwd ? "text" : "password"}
                      required
                      value={loginForm.password}
                      onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                      data-testid="doctor-login-password-input"
                      className="h-11 pr-10 border-slate-300"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                      aria-label={showPwd ? "Hide password" : "Show password"}
                      data-testid="doctor-login-password-toggle"
                    >
                      {showPwd ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
                    </button>
                  </div>
                </div>
                {loginErr && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2" data-testid="doctor-login-error">{loginErr}</div>}
                <Button
                  type="submit"
                  disabled={loginLoading}
                  className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white rounded-full font-semibold shadow-md"
                  data-testid="doctor-login-submit-btn"
                >
                  {loginLoading ? "Signing in…" : "Login"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          <div className="mt-4 text-xs text-slate-500 text-center">
            Looking to book a second opinion instead?{" "}
            <Link to="/login?as=patient" className="text-teal-700 font-semibold hover:underline" data-testid="go-patient-link">Patient signup / login</Link>
          </div>
        </Card>
      </div>
    </div>
  );
}

function CertUpload({ label, hint, testid, file, onChange, required }) {
  const inputRef = useRef(null);
  const MB = (n) => `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return (
    <div>
      <Label className="flex items-center gap-1">
        {label}{required && <span className="text-rose-500">*</span>}
      </Label>
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/*"
        className="hidden"
        onChange={(e) => onChange(e.target.files?.[0] || null)}
        data-testid={`${testid}-input`}
      />
      {!file ? (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="mt-1 w-full h-[72px] rounded-lg border-2 border-dashed border-slate-300 hover:border-teal-400 hover:bg-teal-50/40 transition flex flex-col items-center justify-center gap-0.5 text-slate-500"
          data-testid={`${testid}-btn`}
        >
          <Upload className="w-4 h-4 text-teal-600"/>
          <span className="text-xs font-semibold">Click to upload</span>
          <span className="text-[10px] text-slate-400">{hint}</span>
        </button>
      ) : (
        <div className="mt-1 p-2.5 rounded-lg border border-emerald-200 bg-emerald-50 flex items-center gap-2.5" data-testid={`${testid}-preview`}>
          <FileCheck2 className="w-5 h-5 text-emerald-700 shrink-0"/>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold text-emerald-900 truncate">{file.name}</div>
            <div className="text-[10px] text-emerald-700">{MB(file.size)} · ready to upload</div>
          </div>
          <button
            type="button"
            onClick={() => { onChange(null); if (inputRef.current) inputRef.current.value = ""; }}
            className="text-emerald-700 hover:text-emerald-900 shrink-0 p-1 rounded hover:bg-emerald-100"
            aria-label="Remove file"
            data-testid={`${testid}-remove`}
          >
            <X className="w-4 h-4"/>
          </button>
        </div>
      )}
    </div>
  );
}
